import React from 'react';
import { AlertTriangle, Crown, X } from 'lucide-react';
import { useMonthlyPostsLimit, useScheduledQueueLimit } from '../../hooks/useSocialMediaLimits';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';

interface LimitWarningProps {
  onDismiss?: () => void;
  showDismiss?: boolean;
}

const LimitWarning: React.FC<LimitWarningProps> = ({ onDismiss, showDismiss = true }) => {
  const monthlyLimit = useMonthlyPostsLimit();
  const queueLimit = useScheduledQueueLimit();
  const navigate = useNavigate();

  // Check if we should show warnings
  // Don't show warnings for features that are locked (limit = 0)
  const monthlyWarning = !monthlyLimit.unlimited && 
    monthlyLimit.limit > 0 &&
    monthlyLimit.canPublish && 
    (monthlyLimit.current_count || 0) >= (monthlyLimit.limit || 0) * 0.8;

  const queueWarning = !queueLimit.unlimited && 
    queueLimit.limit > 0 &&
    queueLimit.canSchedule && 
    (queueLimit.current_count || 0) >= (queueLimit.limit || 0) * 0.8;

  const monthlyLimitReached = !monthlyLimit.canPublish && monthlyLimit.limit > 0;
  const queueLimitReached = !queueLimit.canSchedule && queueLimit.limit > 0;

  // Don't show if no warnings
  if (!monthlyWarning && !queueWarning && !monthlyLimitReached && !queueLimitReached) {
    return null;
  }

  const getWarningType = () => {
    if (monthlyLimitReached || queueLimitReached) return 'error';
    if (monthlyWarning || queueWarning) return 'warning';
    return 'info';
  };

  const getWarningMessage = () => {
    if (monthlyLimitReached && queueLimitReached) {
      return 'Both your monthly content and queue limits have been reached.';
    }
    if (monthlyLimitReached) {
      return `Monthly content limit reached (${monthlyLimit.current_count}/${monthlyLimit.limit}). You cannot publish more content this month.`;
    }
    if (queueLimitReached) {
      return `Queue limit reached (${queueLimit.current_count}/${queueLimit.limit}). You cannot schedule more posts.`;
    }
    if (monthlyWarning && queueWarning) {
      return 'You\'re approaching both your monthly content and queue limits.';
    }
    if (monthlyWarning) {
      return `You're approaching your monthly content limit (${monthlyLimit.current_count}/${monthlyLimit.limit} used).`;
    }
    if (queueWarning) {
      return `Your queue is almost full (${queueLimit.current_count}/${queueLimit.limit} scheduled posts).`;
    }
    return '';
  };

  const warningType = getWarningType();
  const message = getWarningMessage();

  const bgColor = {
    error: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    warning: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
    info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
  }[warningType];

  const textColor = {
    error: 'text-red-800 dark:text-red-200',
    warning: 'text-yellow-800 dark:text-yellow-200',
    info: 'text-blue-800 dark:text-blue-200'
  }[warningType];

  const iconColor = {
    error: 'text-red-500',
    warning: 'text-yellow-500',
    info: 'text-blue-500'
  }[warningType];

  return (
    <div className={`rounded-lg border p-4 ${bgColor}`}>
      <div className="flex items-start space-x-3">
        <AlertTriangle className={`h-5 w-5 mt-0.5 ${iconColor}`} />
        <div className="flex-1">
          <div className={`text-sm font-medium ${textColor}`}>
            {warningType === 'error' ? 'Limit Reached' : 'Approaching Limits'}
          </div>
          <div className={`text-sm mt-1 ${textColor}`}>
            {message}
          </div>
          <div className="mt-3 flex items-center space-x-3">
            <Button
              size="sm"
              onClick={() => navigate('/pricing')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <Crown className="w-4 h-4 mr-1" />
              Upgrade Plan
            </Button>
            {warningType === 'warning' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate('/pricing')}
                className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
              >
                View Plans
              </Button>
            )}
          </div>
        </div>
        {showDismiss && onDismiss && (
          <button
            onClick={onDismiss}
            className={`p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 ${textColor}`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default LimitWarning;